/*
  Outcome Enrichment Engine

  This module handles enriching outcome nodes with additional data from the
  general outcomes backend endpoint, including icons, colors, and public IDs.
  It also assigns condition_public_id to edges based on matching outcome nodes.
*/

import type { Outcome } from '../../settings/outcomes/types';
import type { JourneyDocument, OutcomeNode } from '../types';

/**
 * Build a lookup map from outcome labels to their public IDs
 *
 * @param nodes - Object containing all document nodes
 * @returns Map from outcome label to public ID
 */
function buildOutcomeLookupMap(nodes: Record<string, any>): Record<string, string | null> {
  const outcomeNodes = Object.values(nodes).filter(n => n.type === 'outcome') as OutcomeNode[];
  const outcomeMap: Record<string, string | null> = {};

  outcomeNodes.forEach(node => {
    const outcomeLabel = node.config?.outcome;
    const outcomeId = node.config?.customer_outcome_id;
    if (outcomeLabel) {
      outcomeMap[outcomeLabel] = outcomeId || null;
    }
  });

  return outcomeMap;
}

/**
 * Assigns condition_public_id to edges based on matching outcome nodes
 *
 * This function creates a mapping from outcome labels to their public IDs,
 * then updates all edges to include the condition_id based on their
 * condition label.
 *
 * @param doc - The journey document to process
 * @returns The document with condition_public_id assigned to all applicable edges
 */
export function assignConditionPublicIds(doc: JourneyDocument): JourneyDocument {
  const outcomeMap = buildOutcomeLookupMap(doc.nodes);

  // Update edges with condition_public_id
  const newEdges = Object.fromEntries(
    Object.entries(doc.edges).map(([edgeId, edge]) => {
      if (edge.condition && Object.prototype.hasOwnProperty.call(outcomeMap, edge.condition)) {
        return [edgeId, { ...edge, condition_id: outcomeMap[edge.condition] }];
      }
      return [edgeId, edge];
    }),
  );

  return { ...doc, edges: newEdges };
}

/**
 * Build a lookup map from outcome labels to full outcome objects
 *
 * @param backendOutcomes - Array of outcomes from backend
 * @returns Map from outcome label to outcome object
 */
function buildBackendOutcomeLookup(backendOutcomes: Outcome[]): Record<string, Outcome> {
  const lookup: Record<string, Outcome> = {};
  backendOutcomes.forEach(o => {
    lookup[o.outcome] = o;
  });
  return lookup;
}

/**
 * Enrich a single outcome node with backend data
 *
 * @param node - The node to enrich
 * @param backendLookup - Lookup map for backend outcomes
 * @returns The enriched node
 */
function enrichOutcomeNode(
  node: any,
  backendLookup: Record<string, Outcome>,
): any {
  const outcomeLabel = node.config?.outcome;
  const match = outcomeLabel ? backendLookup[outcomeLabel] : undefined;

  return {
    ...node,
    config: {
      ...(node.config || {}),
      customer_outcome_id: match?.customer_outcome_id?.toString() ?? null,
      outcome_icon: match?.icon_name ?? null,
      outcome_color: match?.outcome_color ?? null,
    },
  };
}

/**
 * Enriches outcome nodes with data from the backend outcomes endpoint
 *
 * This function takes outcome data from the backend (including icons, colors,
 * and customer IDs) and merges it into the outcome nodes in the document.
 * After enrichment, it also updates condition_public_id on edges.
 *
 * @param doc - The journey document to enrich
 * @param backendOutcomes - Array of outcomes from backend endpoint
 * @returns The enriched document with full outcome data
 */
export function enrichOutcomeNodes(
  doc: JourneyDocument,
  backendOutcomes: Outcome[],
): JourneyDocument {
  const lookup = buildBackendOutcomeLookup(backendOutcomes);

  // Enrich outcome nodes
  const enrichedNodes = Object.fromEntries(
    Object.entries(doc.nodes).map(([id, node]) => {
      if (node.type !== 'outcome') return [id, node];
      return [id, enrichOutcomeNode(node, lookup)];
    }),
  );

  const enrichedDoc = { ...doc, nodes: enrichedNodes };

  // Update condition_public_id after enrichment
  return assignConditionPublicIds(enrichedDoc);
}

/**
 * Assigns condition_id to ALL edges based on the full outcome map
 *
 * This function should be called after props are loaded (which includes all outcomes).
 * Unlike assignConditionPublicIds which only looks at outcome nodes, this uses
 * a complete outcome map that includes workflow/action outcomes.
 *
 * @param doc - The journey document to process
 * @param outcomeMap - Map from outcome name to customer_outcome_id
 * @returns The document with condition_id assigned to all edges
 */
export function assignConditionIdsFromOutcomes(
  doc: JourneyDocument,
  outcomeMap: Record<string, number | null>,
): JourneyDocument {
  // Update edges with condition_id from outcome map
  const newEdges = Object.fromEntries(
    Object.entries(doc.edges).map(([edgeId, edge]) => {
      if (edge.condition && outcomeMap[edge.condition] !== undefined) {
        const conditionId = outcomeMap[edge.condition];
        return [edgeId, { ...edge, condition_id: conditionId ?? null }];
      }
      return [edgeId, edge];
    }),
  );

  return { ...doc, edges: newEdges };
}
